/**
 * Règles de gestion affichées par le mode « Règles de gestion » (outil interne MOA / recette / dev).
 * Seules des règles effectivement implémentées sont référencées ici : le champ `texte` est écrit pour
 * un lecteur métier (sans jargon technique), le champ `source` indique au dev où elle est codée.
 * Point de départ : manuel technique du portail partenaire (chapitre « Règles de gestion »),
 * \\srv000imd\Projets_clients\Groupe-Herve\PORTAIL PARTENAIRE\Manuel portail partenaire\Manuels_Portail_Partenaire.html
 * Contenu volontairement hors i18n : documentation interne, non traduite, non poussée en base.
 */

export type SectionRegle =
    | 'demandeIntervention'
    | 'demandeDevis'
    | 'demandeDevisSuivi'
    | 'suiviDemandeDevis'
    | 'suiviDemandeIntervention'
    | 'suiviDemandeInterventionDetail'
    | 'planningPreventif'
    | 'suiviInterventionsPreventives'
    | 'suiviInterventionsPreventivesDetail'
    | 'suiviInterventionsNonPreventives'
    | 'suiviInterventionsNonPreventivesDetail'
    | 'rapportsASigner'
    | 'suiviDevis'
    | 'devisDetail'
    | 'rapportsIntervention'
    | 'doe'
    | 'documentsReglementaires'
    | 'equipements'
    | 'equipementDetail'
    | 'tagNfc'
    | 'bim'
    | 'dashboard'
    | 'editionDashboard'
    | 'menuCompte'
    | 'optionsInterventions'
    | 'monCompte'
    | 'preavisIntervention'
    | 'facturation'
    | 'outils'
    | 'reclamation'
    | 'suiviReclamation'
    | 'satisfaction'
    | 'questionnaireSatisfaction'
    | 'contact'
    | 'dashboardFournisseur'
    | 'facturesFournisseur'
    | 'factureFournisseurDetail'
    | 'reglementsFournisseur'
    | 'reglementFournisseurDetail';

export interface RegleGestion {
    cle: string;
    section: SectionRegle;
    titre: string;
    texte: string;
    source: string;
}

export const SECTIONS: Record<SectionRegle, { libelle: string; route: string }> = {
    demandeIntervention: { libelle: "Demande d'intervention", route: '/intervention' },
    demandeDevis: { libelle: 'Demande de devis', route: '/devis' },
    demandeDevisSuivi: { libelle: "Suivi d'une demande de devis", route: '/suivi-demandes-devis/:id' },
    suiviDemandeDevis: { libelle: 'Suivi des demandes de devis', route: '/suivi-demandes-devis' },
    suiviDemandeIntervention: { libelle: "Suivi des demandes d'intervention", route: '/suivi-demande-intervention' },
    suiviDemandeInterventionDetail: {
        libelle: "Suivi d'une demande d'intervention",
        route: '/suivi-demande-intervention/:id',
    },
    planningPreventif: { libelle: 'Planning préventif', route: '/suivi-preventif' },
    suiviInterventionsPreventives: {
        libelle: 'Interventions préventives',
        route: '/suivi-interventions-preventives',
    },
    suiviInterventionsPreventivesDetail: {
        libelle: 'Intervention préventive',
        route: '/suivi-interventions-preventives/:id',
    },
    suiviInterventionsNonPreventives: { libelle: 'Interventions non préventives', route: '/suivi-travaux' },
    suiviInterventionsNonPreventivesDetail: { libelle: 'Intervention non préventive', route: '/suivi-travaux/:id' },
    rapportsASigner: { libelle: 'Rapports à signer', route: '/rapports-a-signer' },
    suiviDevis: { libelle: 'Tous les devis', route: '/suivi-devis' },
    devisDetail: { libelle: 'Devis', route: '/suivi-devis/:id' },
    rapportsIntervention: { libelle: "Rapports d'intervention", route: '/rapports' },
    doe: { libelle: 'DOE', route: '/doe' },
    documentsReglementaires: { libelle: 'Documents réglementaires', route: '/documents' },
    equipements: { libelle: 'Equipements', route: '/equipements' },
    equipementDetail: { libelle: 'Fiche équipement', route: '/equipements/:id' },
    tagNfc: { libelle: 'Tags NFC', route: '/tag-nfc' },
    bim: { libelle: 'B.I.M.', route: '' },
    dashboard: { libelle: 'Tableau de bord', route: '/dashboard' },
    editionDashboard: { libelle: 'Édition du tableau de bord', route: '/edition-dashboard' },
    menuCompte: { libelle: 'Menu du compte', route: '' },
    optionsInterventions: { libelle: 'Options interventions', route: '' },
    monCompte: { libelle: 'Mon compte', route: '' },
    preavisIntervention: { libelle: "Préavis d'intervention", route: '' },
    facturation: { libelle: 'Facturation', route: '/facturation' },
    outils: { libelle: 'Votre suivi énergétique', route: '/outils' },
    reclamation: { libelle: 'Saisir une réclamation', route: '/reclamation' },
    suiviReclamation: { libelle: 'Voir toutes les réclamations', route: '/suivi-reclamation' },
    satisfaction: { libelle: 'Satisfaction', route: '/satisfaction' },
    questionnaireSatisfaction: { libelle: 'Questionnaire de satisfaction', route: '/question/:note/:step' },
    contact: { libelle: 'Contact', route: '/contacts' },
    dashboardFournisseur: { libelle: 'Tableau de bord fournisseur', route: '/dashboard-fournisseur' },
    facturesFournisseur: { libelle: 'Factures fournisseur', route: '/factures-fournisseur' },
    factureFournisseurDetail: { libelle: 'Facture fournisseur', route: '/factures-fournisseur/:id' },
    reglementsFournisseur: { libelle: 'Règlements fournisseur', route: '/reglements-fournisseur' },
    reglementFournisseurDetail: { libelle: 'Règlement fournisseur', route: '/reglement-fournisseur/:id' },
};

export const REGLES_GESTION: RegleGestion[] = [
    {
        cle: 'demandeIntervention.rechercheSiteCritere',
        section: 'demandeIntervention',
        titre: 'Recherche de site',
        texte: 'La recherche se lance automatiquement pendant la saisie, dès le premier caractère.',
        source: 'Front · SearchBar.vue (debounce 600ms) → SiteListService.getSiteList → BFF GET /adresses-sites?recherche=...',
    },
    {
        cle: 'demandeIntervention.listeSites',
        section: 'demandeIntervention',
        titre: 'Vos sites',
        texte: 'Liste des sites actifs auxquels le compte a accès, filtrable par la recherche. Les sites clôturés n\'apparaissent pas ici, contrairement à l\'écran de suivi des demandes qui les inclut.',
        source: 'Front · SelectSite.vue (title « Vos sites ») ; StepOne.vue (inclureTermines non transmis = false) ; ListDemandeInterventionPage.vue (inclureTermines = true, pour comparaison)',
    },
    {
        cle: 'demandeIntervention.droitDemande',
        section: 'demandeIntervention',
        titre: 'Droit de demande',
        texte: 'La création et la relance d\'une demande sont réservées aux comptes ayant le droit de créer une demande. Ce droit est revérifié à chaque action, pas seulement à la connexion : le retirer à un compte prend effet immédiatement.',
        source: 'BFF · DemandeInterventionController.saveDemandeIntervention/saveRelanceDemandeIntervention · JWTAuthorizationFilter.getAuthentication · ProfilExtranetCompte.getFonctions',
    },
    {
        cle: 'demandeIntervention.sauvegardeEtapes',
        section: 'demandeIntervention',
        titre: 'Sauvegarde entre les étapes',
        texte: 'Les informations saisies sont conservées en passant d\'une étape à l\'autre, mais sont perdues si vous quittez la page ou l\'actualisez (F5).',
        source: 'Front · store/mutations.ts (module Intervention) · InterventionPage.beforeRouteLeave → reset()',
    },
    {
        cle: 'demandeIntervention.unSeulSite',
        section: 'demandeIntervention',
        titre: 'Site unique',
        texte: 'S\'il n\'y a qu\'un seul site disponible, il est sélectionné automatiquement : l\'écran de choix du site ne s\'affiche pas.',
        source: 'Front web · StepOne.vue (site unique présélectionné) · SelectSite.vue',
    },
    {
        cle: 'demandeIntervention.delaiIntervention',
        section: 'demandeIntervention',
        titre: "Délai d'intervention",
        texte: 'Les délais proposés sont ceux paramétrés pour le chantier, sans filtre sur leur validité ou sur les dates du contrat. Les délais « autre » sont toujours affichés en fin de liste.',
        source: 'BFF · TypeInterventionService.getTypesIntervention · API intervention · DelaiInterventionRepository.findAllByChantierId',
    },
    {
        cle: 'demandeIntervention.interventionEnCours',
        section: 'demandeIntervention',
        titre: 'Équipement déjà concerné',
        texte: 'Un équipement est considéré comme ayant une intervention « en cours » dès qu\'une demande existe sur un statut autre que « Traitée », quel que soit ce statut.',
        source: 'BFF · DemandeInterventionService.getDemandesInterventionsEnCours · API intervention · DemandeInterventionNonTraiteeSpecification',
    },
    {
        cle: 'demandeIntervention.rechercheEquipementCriteres',
        section: 'demandeIntervention',
        titre: "Recherche d'équipement",
        texte: 'RECHERCHER n\'est actif qu\'avec au moins un des trois champs renseigné : équipement, groupe ou sous-groupe.',
        source: 'Front · StepThree.vue (méthode disabled)',
    },
    {
        cle: 'demandeIntervention.equipementNonTrouve',
        section: 'demandeIntervention',
        titre: 'Équipement non trouvé',
        texte: 'Cocher « Équipement non trouvé » permet de poursuivre sans équipement précis. Ça désélectionne l\'équipement déjà choisi et retire l\'obligation d\'indiquer son état.',
        source: 'Front · StepThree.vue (equipementMissing) · EquipementList.vue (toggleSelect) · getters.ts isInterventionFormValid (étape 3)',
    },
    {
        cle: 'demandeIntervention.etatEquipementObligatoire',
        section: 'demandeIntervention',
        titre: "État de l'équipement",
        texte: 'L\'état de l\'équipement n\'est obligatoire que dans le formulaire web, à l\'étape 3 (sauf si « Équipement non trouvé » est coché). Une demande transmise autrement peut ne pas l\'indiquer.',
        source: 'Front web · getters.ts isInterventionFormValid (étape 3) ; aucune contrainte correspondante sur DemandeInterventionDTO côté BFF',
    },
    {
        cle: 'demandeIntervention.etatEquipementDefaut',
        section: 'demandeIntervention',
        titre: 'État proposé par défaut',
        texte: '« Normal » est présélectionné à l\'ouverture du choix d\'état. Il suffit de cliquer sur un autre état pour le remplacer.',
        source: 'Front · EquipementCondition.vue (beforeMount, présélection état id = 1)',
    },
    {
        cle: 'demandeIntervention.piecesJointes',
        section: 'demandeIntervention',
        titre: 'Pièces jointes',
        texte: 'Facultatif : permet de joindre des photos ou un PDF pour illustrer la demande, transmis au technicien avec celle-ci. Formats acceptés : JPG, PNG, GIF, PDF. Un fichier d\'un autre format, ou portant le même nom qu\'un fichier déjà ajouté, est ignoré sans message.',
        source: 'Front · AddDocuments.vue (validDocType, accept) · InterventionService.sendAskIntervention (pièces jointes envoyées avec la demande)',
    },
    {
        cle: 'demandeIntervention.contactsSuivi',
        section: 'demandeIntervention',
        titre: 'Contacts de suivi',
        texte: 'Ces contacts reçoivent par email les mises à jour de la demande ; ils n\'ont pas besoin d\'être présents sur site. Plusieurs peuvent être cochés, 5 maximum, et au moins un doit rester coché tant que le suivi est activé.',
        source: 'Front · ContactList.vue (toggleSelect, openAdd) ; getters.ts isInterventionFormValid (étape 5, suivi)',
    },
    {
        cle: 'demandeIntervention.contactSurSite',
        section: 'demandeIntervention',
        titre: 'Contact sur site',
        texte: 'Désigne la personne présente sur site que le technicien peut joindre pendant l\'intervention : un seul contact est retenu pour la demande, même si jusqu\'à 5 sont enregistrés. Nom, prénom et téléphone sont obligatoires dans le formulaire web ; une demande transmise autrement peut être envoyée sans contact.',
        source: 'Front · getters.ts isInterventionFormValid, ContactSurSiteList.vue · BFF · DemandeInterventionService.isValid/setDemandeur (aucun contrôle)',
    },
    {
        cle: 'demandeIntervention.telephoneContactSurSite',
        section: 'demandeIntervention',
        titre: 'Format du téléphone',
        texte: 'Le numéro (portable ou fixe) doit contenir entre 2 et 16 chiffres, avec un + facultatif en début. Un seul des deux numéros valides suffit pour continuer ; les espaces et tirets ne sont pas acceptés.',
        source: 'Front · Regex.telephone · getters.ts isInterventionFormValid (étape 5)',
    },
    {
        cle: 'demandeIntervention.suivantEtape5',
        section: 'demandeIntervention',
        titre: 'Activer SUIVANT',
        texte: 'SUIVANT ne s\'active qu\'avec un contact sur site complet (nom, prénom, un téléphone au bon format) et, si le suivi est activé, au moins un contact de suivi coché.',
        source: 'Front · getters.ts isInterventionFormValid (étape 5)',
    },
    {
        cle: 'demandeIntervention.interventionRecurrente',
        section: 'demandeIntervention',
        titre: 'Intervention récurrente',
        texte: 'Marque simplement la demande comme récurrente : aucun champ supplémentaire n\'est demandé et aucun contrôle n\'y est associé.',
        source: 'Front · store/actions.ts (champ recurrence transmis tel quel avec la demande)',
    },
    {
        cle: 'demandeIntervention.suiviDemande',
        section: 'demandeIntervention',
        titre: 'Suivi de la demande',
        texte: 'Activer le suivi de la demande impose, dans le formulaire web, de renseigner au moins un contact de suivi. Une demande transmise autrement peut activer le suivi sans contact.',
        source: 'Front web · getters.ts isInterventionFormValid ; aucun contrôle serveur',
    },
    {
        cle: 'demandeIntervention.astreinte',
        section: 'demandeIntervention',
        titre: 'Demande en astreinte',
        texte: 'Les coordonnées d\'astreinte affichées dépendent de la société du demandeur (Hervé Thermique ou Alvazzi) et du moment de la demande : avant 8h, après 16h30, ou un jour férié.',
        source: 'Front web · store/actions.ts (HT_ASTREINTE, ALVAZZI_ASTREINTE, SHOW_ASTREINTE_MODALE) · SiteService.isHT/isAlvazzi · AstreinteModal.vue · JourFerieModal.vue',
    },

    // --- Demande de devis ---
    {
        cle: 'demandeDevis.listeSites',
        section: 'demandeDevis',
        titre: 'Vos sites',
        texte: 'Liste des sites actifs auxquels le compte a accès, filtrable par la recherche. Les sites clôturés n\'apparaissent pas ici, contrairement à l\'écran de suivi des demandes qui les inclut.',
        source: 'Front · SelectSite.vue (title « Vos sites ») ; EstimatePage.vue (inclureTermines non transmis = false)',
    },
    {
        cle: 'demandeDevis.siteObligatoire',
        section: 'demandeDevis',
        titre: 'Choix du site',
        texte: 'SUIVANT n\'est actif qu\'après avoir choisi un site, même s\'il n\'y en a qu\'un seul : contrairement à la demande d\'intervention, aucun site n\'est présélectionné automatiquement.',
        source: 'Front · EstimatePage.vue (isEstimateFormValid, étape 1) ; SelectSite.vue (aucune présélection)',
    },
    {
        cle: 'demandeDevis.descriptionMinimale',
        section: 'demandeDevis',
        titre: 'Description',
        texte: 'Au moins 50 caractères sont nécessaires pour pouvoir valider la demande ; 4 000 caractères maximum.',
        source: 'Front · EstimateForm.vue (descriptifValue, maxlength) ; getters.ts isEstimateFormValid',
    },
    {
        cle: 'demandeDevis.dateSouhaitee',
        section: 'demandeDevis',
        titre: 'Date souhaitée',
        texte: 'La date ne peut pas être antérieure à aujourd\'hui.',
        source: 'Front · EstimateForm.vue (DatePicker dateMin = today)',
    },
    {
        cle: 'demandeDevis.piecesJointes',
        section: 'demandeDevis',
        titre: 'Pièces jointes',
        texte: 'Facultatif : permet de joindre des photos ou un PDF pour illustrer la demande. Formats acceptés : JPG, PNG, GIF, PDF. Un fichier d\'un autre format, ou portant le même nom qu\'un fichier déjà ajouté, est ignoré sans message.',
        source: 'Front · AddDocuments.vue (validDocType, accept) · EstimateService.sendAskEstimate (pièces jointes envoyées avec la demande)',
    },
    {
        cle: 'demandeDevis.siteNonModifiable',
        section: 'demandeDevis',
        titre: 'Site non modifiable',
        texte: 'En modification, l\'étape de choix du site est sautée et le site n\'est pas modifiable : seuls l\'objet, la description, la date et les pièces jointes peuvent être changés.',
        source: 'Front · EstimatePage.vue (isEditing) ; DemandeDevisSinglePage.vue (openEditEstimate) ; store/actions.ts (UPDATE_ASK_ESTIMATE sans chantierId/siteId)',
    },

    // --- Suivi d'une demande de devis ---
    {
        cle: 'suiviDemandeDevis.portee',
        section: 'suiviDemandeDevis',
        titre: 'Demandes affichées',
        texte: 'Liste les demandes de devis envoyées ; « Mes demandes » filtre pour n\'afficher que celles envoyées par le compte connecté. Les données sont interrogées en direct à chaque ouverture de la page : le portail les affiche sans les modifier, l\'avancement est mis à jour par les équipes commerciales.',
        source: 'Front · ListDemandeDevisPage.vue (filter.isMine, GET_DEMANDES_DEVIS)',
    },
    {
        cle: 'suiviDemandeDevis.statuts',
        section: 'suiviDemandeDevis',
        titre: 'Statuts d\'une demande',
        texte: 'Une demande de devis passe par les statuts En attente, En chiffrage, puis Devis publié.',
        source: 'Front · models/DemandeDevis.ts (EtatDemandeDevis) ; locales/fr.json (state.*)',
    },
    {
        cle: 'suiviDemandeDevis.devisLies',
        section: 'suiviDemandeDevis',
        titre: 'Devis liés',
        texte: 'Affiche les numéros et versions des devis déjà publiés pour cette demande. Reste vide tant qu\'aucun devis n\'a été publié.',
        source: 'Front · ListDemandeDevisPage.vue (devisIds)',
    },
    {
        cle: 'demandeDevisSuivi.modificationLimitee',
        section: 'demandeDevisSuivi',
        titre: 'Modifier / Supprimer',
        texte: 'MODIFIER et SUPPRIMER ne sont proposés que tant que la demande est au statut « En attente ».',
        source: 'Front · DemandeDevisSinglePage.vue (isEnAttente)',
    },

    // --- Suivi des demandes d'intervention (liste) ---
    {
        cle: 'suiviDemandeIntervention.portee',
        section: 'suiviDemandeIntervention',
        titre: 'Demandes affichées',
        texte: 'Liste toutes les demandes d\'intervention envoyées pour les sites auxquels le compte a accès, quel que soit leur auteur : pas seulement celles du compte connecté. Les données sont interrogées en direct à chaque ouverture de la page auprès du système de gestion des interventions : le portail les affiche sans les modifier, l\'avancement (statut, rapport) est mis à jour par les équipes d\'intervention.',
        source: 'Front · ListDemandeInterventionPage.vue (getDemandesInterventionData, aucun filtre par demandeur appliqué)',
    },
    {
        cle: 'suiviDemandeIntervention.statuts',
        section: 'suiviDemandeIntervention',
        titre: 'Statuts d\'une demande',
        texte: 'Une demande passe par les statuts Non planifiée, Planifiée, En cours, Traitée ou Refusée. Le statut est piloté par le système d\'intervention : il n\'est pas modifiable depuis le portail, seule une demande Non planifiée peut encore y être relancée ou supprimée.',
        source: 'Front · models/DemandeIntervention.ts (EtatDemandeIntervention) ; locales/fr.json (state.*) ; DemandeInterventionSinglePage.vue (isNonPlanifiee)',
    },
    {
        cle: 'suiviDemandeIntervention.rapport',
        section: 'suiviDemandeIntervention',
        titre: 'Rapport',
        texte: 'L\'icône PDF télécharge le rapport d\'intervention correspondant à la demande ; elle n\'apparaît que si un rapport a déjà été généré.',
        source: 'Front · ListDemandeInterventionPage.vue (downloadPdf, rapportPresent) ; DemandeInterventionService.getRapportsPDF',
    },
    {
        cle: 'suiviDemandeIntervention.filtreSiteUnique',
        section: 'suiviDemandeIntervention',
        titre: 'Filtre Site',
        texte: 'Le filtre Site n\'est affiché que si le compte a accès à plusieurs sites. Avec un seul site, il n\'y a rien à filtrer et le filtre est masqué.',
        source: 'Front · ListDemandeInterventionPage.vue (isUniqueSite)',
    },
    {
        cle: 'suiviDemandeIntervention.selectionTout',
        section: 'suiviDemandeIntervention',
        titre: 'Tout sélectionner = aucun filtre',
        texte: 'Sélectionner tous les sites, ou tous les statuts, revient à ne filtrer sur rien : la liste affiche toutes les demandes.',
        source: 'Front · ListDemandeInterventionPage.vue (getDemandesInterventionData)',
    },
    {
        cle: 'suiviDemandeIntervention.exporterActif',
        section: 'suiviDemandeIntervention',
        titre: 'Exporter',
        texte: 'EXPORTER n\'est actif que si au moins un site et un statut sont sélectionnés, et qu\'il y a des résultats à exporter.',
        source: 'Front · ListDemandeInterventionPage.vue (isExportDisabled)',
    },

    // --- Suivi d'une demande d'intervention (détail) ---
    {
        cle: 'suiviDemandeInterventionDetail.actionsNonPlanifiee',
        section: 'suiviDemandeInterventionDetail',
        titre: 'Supprimer / Relancer',
        texte: 'SUPPRIMER et RELANCER ne sont proposés que si la demande est au statut « Non planifiée ».',
        source: 'Front · DemandeInterventionSinglePage.vue (isNonPlanifiee)',
    },
    {
        cle: 'suiviDemandeInterventionDetail.rapportDisponible',
        section: 'suiviDemandeInterventionDetail',
        titre: 'Rapport PDF',
        texte: 'Le téléchargement du rapport n\'est proposé que si un rapport existe déjà pour l\'intervention correspondante.',
        source: 'Front · DemandeInterventionSinglePage.vue (isRapportPresent) ; DataList.vue (downloadPdf)',
    },

    // --- Planning préventif ---
    {
        cle: 'planningPreventif.portee',
        section: 'planningPreventif',
        titre: 'Planning affiché',
        texte: 'Affiche, sous forme de grille par période, les interventions préventives planifiées par équipement à partir des contrats de maintenance. Les données sont interrogées en direct auprès du système de gestion des interventions à chaque changement de période, de site ou de vue : le portail les affiche sans les modifier.',
        source: 'Front · SuiviPreventif.vue (reload, getData, PreventifActions.GET_PREVENTIF)',
    },
    {
        cle: 'planningPreventif.filtreSiteUnique',
        section: 'planningPreventif',
        titre: 'Choix du site',
        texte: 'Le changement de site n\'est proposé que si le compte a accès à plusieurs sites.',
        source: 'Front · SuiviPreventif.vue (isUniqueSite)',
    },
    {
        cle: 'planningPreventif.choixUnOuTousSites',
        section: 'planningPreventif',
        titre: 'Un site / Tous mes sites',
        texte: '« Un site » affiche le planning préventif du site choisi. « Tous mes sites » agrège le planning de tous les sites auxquels le compte a accès, sans distinction par site.',
        source: 'Front · ModalChoixSite.vue (toogleAllSite, isAllSites) ; SuiviPreventif.vue (validerSite, site = undefined si Tous mes sites)',
    },
    {
        cle: 'planningPreventif.visionPeriode',
        section: 'planningPreventif',
        titre: 'Vision mensuelle / semaine',
        texte: 'Ce switch change le regroupement des périodes affichées sur le planning, par mois ou par semaine.',
        source: 'Front · SuiviPreventif.vue (switchVision, SWITCH_PERIODICITE)',
    },
    {
        cle: 'planningPreventif.visionReel',
        section: 'planningPreventif',
        titre: 'Prévisionnel / réel',
        texte: 'Ce switch bascule le planning entre les dates prévisionnelles et les dates réelles des interventions.',
        source: 'Front · SuiviPreventif.vue (switchIntervention, SWITCH_PREVISIONNEL_REEL)',
    },
    {
        cle: 'planningPreventif.selectionRapports',
        section: 'planningPreventif',
        titre: 'Sélection de rapports',
        texte: 'Le mode sélection permet de cocher plusieurs cases du planning pour télécharger leurs rapports. Une seule case cochée télécharge directement le PDF ; plusieurs demandent de nommer le fichier zip.',
        source: 'Front · SuiviPreventif.vue (toggleSelectionMode, downloadSelected, downloadDocuments)',
    },

    // --- Interventions préventives (liste) ---
    {
        cle: 'suiviInterventionsPreventives.portee',
        section: 'suiviInterventionsPreventives',
        titre: 'Interventions affichées',
        texte: 'Liste les interventions préventives planifiées à partir du planning (contrats de maintenance), pour les sites auxquels le compte a accès. Les données sont interrogées en direct à chaque ouverture de la page auprès du système de gestion des interventions : le portail les affiche sans les modifier, l\'avancement est mis à jour par les équipes d\'intervention.',
        source: 'Front · ListInterventionPreventivePage.vue (GET_SUIVIS_INTERVENTION, module SuiviInterventionsPreventives)',
    },
    {
        cle: 'suiviInterventionsPreventives.statuts',
        section: 'suiviInterventionsPreventives',
        titre: 'Statuts d\'une intervention',
        texte: 'Une intervention préventive passe par les statuts À réaliser, En cours, puis Réalisé.',
        source: 'Front · models/InterventionNonPreventive.ts (EtatsTravaux) ; locales/fr.json (state.*)',
    },
    {
        cle: 'suiviInterventionsPreventives.rapportDisponible',
        section: 'suiviInterventionsPreventives',
        titre: 'Rapport',
        texte: 'L\'icône PDF télécharge le rapport de l\'intervention ; elle n\'apparaît que si un rapport a déjà été généré.',
        source: 'Front · ListInterventionPreventivePage.vue (downloadPdf, isRapportPresent)',
    },
    {
        cle: 'suiviInterventionsPreventives.filtreSiteUnique',
        section: 'suiviInterventionsPreventives',
        titre: 'Filtre Site',
        texte: 'Le filtre Site n\'est affiché que si le compte a accès à plusieurs sites.',
        source: 'Front · ListInterventionPreventivePage.vue (isUniqueSite)',
    },
    {
        cle: 'suiviInterventionsPreventives.filtresEnCascade',
        section: 'suiviInterventionsPreventives',
        titre: 'Filtres en cascade',
        texte: 'Choisir un Groupe limite automatiquement les Sous-groupes et Équipements proposés à ceux de ce groupe, et choisir un Sous-groupe fait de même pour les Équipements.',
        source: 'Front · ListInterventionPreventivePage.vue (refreshSousGroupe, refreshEquipement, refreshEquipementBySousGroupe)',
    },
    {
        cle: 'suiviInterventionsPreventives.exportLimiteEquipements',
        section: 'suiviInterventionsPreventives',
        titre: 'Limite de l\'export',
        texte: 'EXPORTER est refusé si le filtre Équipement porte sur plus de 500 équipements.',
        source: 'Front · ListInterventionPreventivePage.vue (exportXls)',
    },

    // --- Intervention préventive (détail) ---
    {
        cle: 'suiviInterventionsPreventivesDetail.rapportDisponible',
        section: 'suiviInterventionsPreventivesDetail',
        titre: 'Rapport PDF',
        texte: 'Le téléchargement du rapport n\'est proposé que si un rapport existe déjà pour cette intervention.',
        source: 'Front · InterventionPreventiveDetailPage.vue (fichierExistant)',
    },

    // --- Interventions non préventives (liste) ---
    {
        cle: 'suiviInterventionsNonPreventives.portee',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Interventions affichées',
        texte: 'Liste les interventions non préventives (travaux ponctuels, hors contrat de maintenance) réalisées ou en cours, pour les sites auxquels le compte a accès. Les données sont interrogées en direct à chaque ouverture de la page auprès du système de gestion des interventions : le portail les affiche sans les modifier, l\'avancement est mis à jour par les équipes d\'intervention.',
        source: 'Front · ListInterventionPage.vue (GET_SUIVIS_INTERVENTION, module SuiviInterventions)',
    },
    {
        cle: 'suiviInterventionsNonPreventives.statuts',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Statuts d\'une intervention',
        texte: 'Une intervention non préventive passe par les statuts À réaliser, En cours, puis Réalisé.',
        source: 'Front · models/InterventionNonPreventive.ts (EtatsTravaux) ; locales/fr.json (state.*)',
    },
    {
        cle: 'suiviInterventionsNonPreventives.rapportDisponible',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Rapport',
        texte: 'L\'icône PDF télécharge le rapport de l\'intervention ; elle n\'apparaît que si un rapport a déjà été généré.',
        source: 'Front · ListInterventionPage.vue (downloadPdf, isRapportPresent, module SuiviInterventions)',
    },
    {
        cle: 'suiviInterventionsNonPreventives.alarmante',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Alarmante',
        texte: 'Indique que la première observation saisie sur l\'intervention a été marquée « alarme » par l\'intervenant.',
        source: 'Front · InterventionDetailPage.vue (observations[0].alarme) ; ListInterventionPage.vue (isAlarmante)',
    },
    {
        cle: 'suiviInterventionsNonPreventives.filtreSiteUnique',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Filtre Site',
        texte: 'Le filtre Site n\'est affiché que si le compte a accès à plusieurs sites.',
        source: 'Front · ListInterventionPage.vue (isUniqueSite, module SuiviInterventions)',
    },
    {
        cle: 'suiviInterventionsNonPreventives.selectionTout',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Tout sélectionner = aucun filtre',
        texte: 'Sélectionner tous les sites, ou tous les statuts, revient à ne filtrer sur rien : la liste affiche toutes les interventions.',
        source: 'Front · ListInterventionPage.vue (getInterventionData, module SuiviInterventions)',
    },
    {
        cle: 'suiviInterventionsNonPreventives.exporterActif',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Exporter',
        texte: 'EXPORTER n\'est actif que si au moins un site et un statut sont sélectionnés, et qu\'il y a des résultats à exporter.',
        source: 'Front · ListInterventionPage.vue (isExportDisabled, module SuiviInterventions)',
    },

    // --- Intervention non préventive (détail) ---
    {
        cle: 'suiviInterventionsNonPreventivesDetail.rapportDisponible',
        section: 'suiviInterventionsNonPreventivesDetail',
        titre: 'Rapport PDF',
        texte: 'Le téléchargement du rapport n\'est proposé que si un rapport existe déjà pour cette intervention.',
        source: 'Front · InterventionDetailPage.vue (isRapportPresent, module SuiviInterventions)',
    },

    // --- Rapports à signer ---
    {
        cle: 'rapportsASigner.portee',
        section: 'rapportsASigner',
        titre: 'Rapports affichés',
        texte: 'Liste les rapports d\'intervention en attente de signature du client, pour les sites auxquels le compte a accès. Les données sont interrogées en direct à chaque ouverture de la page : un rapport sort de cette liste dès qu\'il est signé.',
        source: 'Front · RapportASigner.vue (GET_RAPPORTS_INTERVENTIONS) ; BFF · GetRapportsASigner',
    },
    {
        cle: 'rapportsASigner.filtreSiteUnique',
        section: 'rapportsASigner',
        titre: 'Filtre Site',
        texte: 'Le filtre Site n\'est affiché que si le compte a accès à plusieurs sites.',
        source: 'Front · RapportASigner.vue (isUniqueSite)',
    },
    {
        cle: 'rapportsASigner.selectionPourSignature',
        section: 'rapportsASigner',
        titre: 'Sélection pour signature',
        texte: 'Cliquer sur une ligne du tableau la coche pour la signature groupée ; contrairement aux autres écrans de suivi, cela n\'ouvre pas le détail de l\'intervention.',
        source: 'Front · RapportASigner.vue (selectRapport)',
    },
    {
        cle: 'rapportsASigner.signerActif',
        section: 'rapportsASigner',
        titre: 'Signer',
        texte: 'SIGNER n\'est actif que si au moins un rapport est coché dans la liste.',
        source: 'Front · RapportASigner.vue (isSignerActivated)',
    },

    // --- Voir tous les devis (liste) ---
    {
        cle: 'suiviDevis.portee',
        section: 'suiviDevis',
        titre: 'Devis affichés',
        texte: 'Par défaut, seuls les devis au statut Publié des 3 derniers mois sont affichés ; les filtres Statut et Date permettent d\'élargir la recherche. Les données sont interrogées en direct à chaque ouverture de la page auprès du système de gestion des devis : le portail les affiche sans les modifier, l\'avancement est mis à jour par les équipes commerciales.',
        source: 'Front · store/state.ts (filterStatus par défaut PUBLIE, filterDateDebut = -3 mois) ; ListDevisPage.vue (GET_DEVIS)',
    },
    {
        cle: 'suiviDevis.statuts',
        section: 'suiviDevis',
        titre: 'Statuts d\'un devis',
        texte: 'Un devis passe par les statuts Publié, En signature, En commande, Transmis, En cours, Réalisé, ou Refusé.',
        source: 'Front · models/Devis.ts (EtatDevis) ; locales/fr.json (state.*)',
    },
    {
        cle: 'suiviDevis.demandeLiee',
        section: 'suiviDevis',
        titre: 'Demande liée',
        texte: 'Affiche, à titre d\'information, le numéro de la demande de devis à l\'origine de ce devis : ce n\'est pas un lien cliquable depuis ce tableau.',
        source: 'Front · ListDevisPage.vue (demandeDevisId) ; DataTable.vue (aucun rendu spécial pour cette colonne)',
    },
    {
        cle: 'suiviDevis.facturation',
        section: 'suiviDevis',
        titre: 'Montant facturé / N° de facture',
        texte: 'Ces deux colonnes restent vides tant que le devis n\'a pas été facturé ; elles se remplissent une fois la ou les factures émises.',
        source: 'Front · ListDevisPage.vue (montantFacture, numFactures)',
    },

    // --- Voir tous les devis (détail) ---
    {
        cle: 'devisDetail.actionsSelonStatut',
        section: 'devisDetail',
        titre: 'Refuser / Commander',
        texte: 'REFUSER et COMMANDER ne sont proposés que si le devis n\'est pas déjà Refusé, En commande, Transmis, En signature ou Réalisé.',
        source: 'Front · DevisDetailPage.vue (validStatus)',
    },
    {
        cle: 'devisDetail.pdfDisponible',
        section: 'devisDetail',
        titre: 'Voir le PDF',
        texte: 'VOIR LE PDF n\'est actif que si le fichier du devis existe déjà côté système.',
        source: 'Front · DevisDetailPage.vue (isFichierPdfPresent)',
    },
    {
        cle: 'devisDetail.commanderDeuxModes',
        section: 'devisDetail',
        titre: 'Commander',
        texte: 'COMMANDER propose deux modes : la signature électronique (DocuSign), ou l\'envoi classique d\'un numéro de commande avec bon de commande joint.',
        source: 'Front · DevisDetailPage.vue (commanderDevis, commanderDevisSignatureElectronique) ; ModalCommand.vue',
    },

    // --- Rapports d'intervention ---
    {
        cle: 'rapportsIntervention.portee',
        section: 'rapportsIntervention',
        titre: 'Rapports affichés',
        texte: 'N\'affiche que les rapports des interventions préventives : la case « Interventions préventives » est fixe, non cliquable, il n\'existe pas d\'équivalent pour les interventions non préventives sur cet écran. Les données sont interrogées en direct à chaque recherche auprès du système de gestion des interventions.',
        source: 'Front · Rapport.vue (isPreventif toujours true, RadioBox sans gestionnaire de clic)',
    },
    {
        cle: 'rapportsIntervention.filtresEnCascade',
        section: 'rapportsIntervention',
        titre: 'Filtres en cascade',
        texte: 'Choisir un Groupe limite automatiquement les Sous-groupes et Équipements proposés à ceux de ce groupe, et choisir un Sous-groupe fait de même pour les Équipements.',
        source: 'Front · Rapport.vue (refreshSousGroupe, refreshEquipement, refreshEquipementBySousGroupe)',
    },
    {
        cle: 'rapportsIntervention.telechargementGroupe',
        section: 'rapportsIntervention',
        titre: 'Téléchargement groupé',
        texte: 'Cocher plusieurs lignes puis télécharger : une seule sélection télécharge directement le PDF ; plusieurs demandent de nommer le fichier zip.',
        source: 'Front · Rapport.vue (getDocuments, downloadDocuments)',
    },
    {
        cle: 'rapportsIntervention.telechargerTout',
        section: 'rapportsIntervention',
        titre: 'Télécharger tout',
        texte: 'Porte sur tous les résultats de la recherche en cours, pas seulement la page affichée. Au-delà de 100 rapports, une étape supplémentaire de confirmation est demandée avant le téléchargement.',
        source: 'Front · Rapport.vue (downloadAllDocuments, ModalDownloadDocuments, seuil de 100)',
    },

    // --- DOE ---
    {
        cle: 'doe.portee',
        section: 'doe',
        titre: 'DOE affichés',
        texte: 'Liste uniquement les DOE publiés, pour tous les sites auxquels le compte a accès : aucun filtre n\'est disponible sur cet écran. Cliquer sur une ligne télécharge directement son PDF.',
        source: 'Front · DOE.vue (GET_DOE_PUBLIE, doSelect → GET_PDF_DOE)',
    },

    // --- Documents réglementaires ---
    {
        cle: 'documentsReglementaires.portee',
        section: 'documentsReglementaires',
        titre: 'Documents affichés',
        texte: 'Liste les documents réglementaires (ou les fiches Analyse de risque / Prise en charge selon le type choisi) pour les sites accessibles. Les données sont interrogées en direct à chaque recherche auprès du système documentaire : le portail les affiche sans les modifier.',
        source: 'Front · DocumentReglementairePage.vue (GET_DOCUMENT_REGLEMENTAIRE, GET_ADR_PDF, GET_PRISE_EN_CHARGE_PDF)',
    },
    {
        cle: 'documentsReglementaires.choixTypeObligatoire',
        section: 'documentsReglementaires',
        titre: 'Choix du type de document',
        texte: 'La page impose de choisir un ou plusieurs types de documents avant d\'afficher des résultats ; le tableau et les filtres changent selon le type choisi (générique, Analyse de risque, ou Prise en charge).',
        source: 'Front · DocumentReglementairePage.vue (displayModalTypeDocument, validerTypes)',
    },
    {
        cle: 'documentsReglementaires.filtresDesactives',
        section: 'documentsReglementaires',
        titre: 'Filtres désactivés',
        texte: 'Les filtres Site, Groupe, Sous-groupe et Équipement sont désactivés quand le type choisi est Analyse de risque ou Prise en charge : ces documents ne sont pas rattachés à un équipement.',
        source: 'Front · DocumentReglementairePage.vue (isAnalyseRisqueOrPriseEnChargeSelected)',
    },
    {
        cle: 'documentsReglementaires.telechargementIndisponiblePriseEnCharge',
        section: 'documentsReglementaires',
        titre: 'Téléchargement groupé indisponible',
        texte: 'Pour le type Prise en charge, le téléchargement groupé n\'est pas proposé : chaque document se télécharge individuellement depuis sa ligne.',
        source: 'Front · DocumentReglementairePage.vue (isPriseEnChargeSelected, boutons de téléchargement masqués)',
    },
    {
        cle: 'documentsReglementaires.definition',
        section: 'documentsReglementaires',
        titre: 'Documents réglementaires',
        texte: 'Documents attestant de la conformité réglementaire d\'un équipement : fiche d\'intervention, certificat d\'étanchéité, attestation d\'entretien chaudière, certificat de ramonage, contrôle de disconnecteurs, attestation d\'entretien thermodynamique, fiche de débit d\'air... Les fiches Analyse de risque et Prise en charge, gérées séparément sur cet écran, n\'en font pas partie.',
        source: 'Front · locales/fr.json (document.type.1 à document.type.9)',
    },
    {
        cle: 'doe.definition',
        section: 'doe',
        titre: 'DOE',
        texte: 'DOE : Dossier des Ouvrages Exécutés. Dossier technique remis à la fin d\'un chantier (plans, notices, certificats) pour le projet concerné.',
        source: 'Front · models/DOEDTO.ts (libelleChantier, libelleSite)',
    },
    {
        cle: 'rapportsIntervention.definition',
        section: 'rapportsIntervention',
        titre: 'Rapport d\'intervention',
        texte: 'Document rédigé par le technicien à l\'issue d\'une intervention préventive, décrivant les travaux réalisés sur l\'équipement.',
        source: 'Front · Rapport.vue ; models/DocumentReglementaire.ts (interventionId, dateIntervention)',
    },

    // --- Equipements (recherche) ---
    {
        cle: 'equipements.definition',
        section: 'equipements',
        titre: 'Recherche d\'équipement',
        texte: 'Permet de retrouver un équipement du parc (chauffage, ventilation, climatisation...) en choisissant un site puis en filtrant par nom, groupe ou sous-groupe, pour ouvrir sa fiche détaillée.',
        source: 'Front · RechercheEquipement.vue',
    },

    // --- Equipements (fiche détail) ---
    {
        cle: 'equipementDetail.definition',
        section: 'equipementDetail',
        titre: 'Fiche équipement',
        texte: 'Fiche d\'un équipement du parc : tableau de bord, historique des demandes d\'intervention et des travaux, devis liés, caractéristiques, gamme de maintenance et documents réglementaires associés.',
        source: 'Front · Equipements.vue (onglets de la fiche)',
    },
    {
        cle: 'equipementDetail.preavisUnique',
        section: 'equipementDetail',
        titre: 'Créer un préavis',
        texte: 'Le bouton de création de préavis n\'apparaît que si aucun préavis n\'existe déjà pour cet équipement.',
        source: 'Front · Equipements.vue (hasPreavisForEquipement)',
    },

    // --- Tags NFC ---
    {
        cle: 'tagNfc.definition',
        section: 'tagNfc',
        titre: 'Tag NFC',
        texte: 'Un tag NFC identifie un emplacement physique (local, équipement) du site. Chaque lecture du tag enregistre une entrée d\'historique (date, localisation).',
        source: 'Front · TagNfcPage.vue ; models/NFCHistorique.ts',
    },
    {
        cle: 'tagNfc.portee',
        section: 'tagNfc',
        titre: 'Tags affichés',
        texte: 'Liste uniquement les tags actifs, pour les sites auxquels le compte a accès : il n\'existe pas de filtre pour afficher les tags inactifs sur cet écran. Les données sont interrogées en direct à chaque recherche.',
        source: 'Front · TagNfcPage.vue (getNFCData, isActif toujours à true)',
    },
    {
        cle: 'tagNfc.historique',
        section: 'tagNfc',
        titre: 'Historique',
        texte: 'Cliquer sur une ligne affiche l\'historique des lectures de ce tag (date et localisation), du plus récent au plus ancien.',
        source: 'Front · TagNfcPage.vue (getHistorique)',
    },

    // --- B.I.M. ---
    {
        cle: 'bim.definition',
        section: 'bim',
        titre: 'B.I.M.',
        texte: 'B.I.M. (Building Information Modeling) : la maquette numérique 3D d\'un bâtiment. Le menu n\'ouvre pas d\'écran dans le portail : il affiche seulement si le compte est connecté à une maquette ou non.',
        source: 'Front · Layout.vue (onItemClick, item.href === \'/bim\') ; menuState/getStatutBim',
    },
    {
        cle: 'equipementDetail.ongletsReutilises',
        section: 'equipementDetail',
        titre: 'Onglets réutilisant les écrans globaux',
        texte: 'Les onglets Tableau de bord et Historique de cette fiche réutilisent les mêmes composants que le tableau de bord d\'accueil et les écrans de suivi globaux (demandes, travaux, devis, documents réglementaires), mais limités à cet équipement : un clic dans un graphique ou une ligne reste dans la fiche au lieu d\'ouvrir l\'écran global.',
        source: 'Front · DashboardTab.vue (prop isBim=true transmis aux widgets du tableau de bord) ; HistoriqueTravauxTab.vue, HistoriqueDemandeInterventionsTab.vue, HistoriqueDevisTab.vue, DocumentsReglementairesTab.vue (isBim: true)',
    },

    // --- Tableau de bord ---
    {
        cle: 'dashboard.personnalisableClientUniquement',
        section: 'dashboard',
        titre: 'Tableau de bord personnalisable',
        texte: 'Plusieurs tableaux de bord peuvent être créés, chacun avec ses propres widgets disposés librement. Cette personnalisation (favoris, duplication, suppression, ajout de widgets) n\'existe que côté client ; le portail fournisseur n\'a pas de tableau de bord personnalisable.',
        source: 'Front · HomePage.vue (isClient) ; EditionDashboard.vue',
    },
    {
        cle: 'dashboard.dashboardAffichePardDefaut',
        section: 'dashboard',
        titre: 'Tableau de bord affiché par défaut',
        texte: 'À la connexion, le portail affiche le dernier tableau de bord consulté dans la session ; à défaut, le tableau de bord marqué favori ; à défaut, le premier de la liste.',
        source: 'Front · Home/store/actions.ts (GET_DASHBOARDS)',
    },
    {
        cle: 'dashboard.favoriIrreversibleIci',
        section: 'dashboard',
        titre: 'Mettre en favori',
        texte: 'Marquer un tableau de bord comme favori est possible en un clic. Une fois favori, recliquer sur l\'étoile ne le retire pas : un message s\'affiche à la place.',
        source: 'Front · HomePage.vue (mettreFavori) ; EditionDashboard.vue (mettreFavori)',
    },
    {
        cle: 'dashboard.suppressionFavoriInterdite',
        section: 'dashboard',
        titre: 'Supprimer un tableau de bord',
        texte: 'SUPPRIMER est refusé pour le tableau de bord marqué favori : un message s\'affiche à la place de la suppression.',
        source: 'Front · HomePage.vue (supprimer) ; EditionDashboard.vue (supprimer)',
    },

    // --- Édition du tableau de bord ---
    {
        cle: 'editionDashboard.widgetFacturationReserve',
        section: 'editionDashboard',
        titre: 'Widget Facturation',
        texte: 'Le widget Facturation a été retiré de la liste proposée dans AJOUTER UN WIDGET : il n\'est plus possible d\'en créer un nouveau. S\'il existe déjà sur un tableau de bord (ajouté avant ce retrait, ou présent sur un tableau de bord par défaut), il continue de s\'afficher normalement.',
        source: 'Front · ModaleCreationWidget.vue (listeWidgets filtre widget.id !== 5) ; EditionDashboard.vue (typeWidgetId === 5, EditionBilling)',
    },
    {
        cle: 'editionDashboard.widgetTaillesDifferentes',
        section: 'editionDashboard',
        titre: 'Taille des widgets',
        texte: 'Le widget Interventions préventives occupe une place double par rapport aux autres widgets (demandes, devis, facturation...) sur la grille.',
        source: 'Front · EditionDashboard.vue (typeWidgetId === 2, width-65pct vs width-32pct)',
    },
    {
        cle: 'editionDashboard.glisserDeposer',
        section: 'editionDashboard',
        titre: 'Réorganiser les widgets',
        texte: 'Les widgets se réordonnent par glisser-déposer ; leur nouvelle position est enregistrée automatiquement, sans bouton de validation.',
        source: 'Front · EditionDashboard.vue (changerPosition, CHANGE_WIDGET_POSITION)',
    },
    {
        cle: 'editionDashboard.enregistrementAutomatique',
        section: 'editionDashboard',
        titre: 'Enregistrement automatique',
        texte: 'Toutes les actions sur les widgets (ajout, suppression, duplication, modification du filtre, repositionnement) sont enregistrées automatiquement. L\'indicateur en haut de l\'écran (« Enregistrement en cours » / « Enregistré ») confirme la sauvegarde, il n\'y a pas de bouton VALIDER séparé.',
        source: 'Front · EditionDashboard.vue (enregistrementEnCours)',
    },
    {
        cle: 'editionDashboard.renommageALaSortie',
        section: 'editionDashboard',
        titre: 'Renommer le tableau de bord',
        texte: 'Contrairement aux autres modifications, le nouveau nom du tableau de bord n\'est enregistré qu\'au clic sur QUITTER, pas au fil de la frappe.',
        source: 'Front · EditionDashboard.vue (libelleDashboard, goDashboard)',
    },

    // --- Tableau de bord : widgets ---
    {
        cle: 'dashboard.widgetFacturationDroit',
        section: 'dashboard',
        titre: 'Widget Facturation',
        texte: 'Le widget Facturation ne s\'affiche que pour un utilisateur ayant le droit de consulter les factures reçues, même si ce widget est présent sur le tableau de bord (ex. via un tableau de bord par défaut).',
        source: 'Front · HomeMain.vue (hasRightFinancier, droit consulter.facture.recue)',
    },
    {
        cle: 'dashboard.widgetPerimetreCommun',
        section: 'dashboard',
        titre: 'Périmètre des widgets',
        texte: 'Chaque widget affiche des compteurs calculés sur son propre périmètre de sites (tous les sites du tableau de bord si aucun n\'a été choisi à sa création) ; cliquer sur un compteur ouvre la liste correspondante, déjà filtrée sur ce même périmètre.',
        source: 'Front · Billing.vue, Estimate.vue, Preventive.vue, PreventiveOut.vue, InterventionRequest.vue (navigateTo/navigationTo)',
    },
    {
        cle: 'dashboard.widgetDevisFenetreFixe',
        section: 'dashboard',
        titre: 'Widget Devis',
        texte: 'Les compteurs du widget Devis portent sur une fenêtre fixe des 3 derniers mois, non modifiable depuis le tableau de bord.',
        source: 'Front · Home/store/modules/EstimateWidget.ts (dateDebut = -3 mois, dateFin = aujourd\'hui)',
    },
    {
        cle: 'dashboard.widgetPreventifFenetres',
        section: 'dashboard',
        titre: 'Widget Préventif',
        texte: 'Le widget Préventif combine deux fenêtres distinctes : le graphique en barres couvre de 5 mois avant à 6 mois après le mois en cours (travaux passés et planifiés), tandis que le disque de synthèse porte sur les 12 derniers mois glissants jusqu\'à la fin du mois en cours.',
        source: 'Front · Preventive.vue (dateDebut/dateFin, dateDebutCircleData/dateFinMois)',
    },

    // --- Tableau de bord : modales de contexte ---
    {
        cle: 'dashboard.choixPortailModale',
        section: 'dashboard',
        titre: 'Choix client / fournisseur',
        texte: 'La modale de choix du portail (client ou fournisseur) ne s\'affiche qu\'à la connexion d\'un compte possédant les deux profils à la fois ; elle ne réapparaît pas une fois un choix fait dans la session.',
        source: 'Front · HomePage.vue (isModaleOpen, isClientAndFournisseur, menuState/getChoixMenu)',
    },
    {
        cle: 'dashboard.communicationModale',
        section: 'dashboard',
        titre: 'Modale de communication',
        texte: 'Une communication s\'affiche au chargement du tableau de bord si le back en a une à montrer. Cocher « ne plus voir » l\'écarte définitivement ; sans cette case cochée, elle est seulement marquée comme vue et peut réapparaître.',
        source: 'Front · HomePageModale.vue (DONT_SHOW / SEEN)',
    },
    {
        cle: 'dashboard.sondageSatisfactionConditions',
        section: 'dashboard',
        titre: 'Rappel enquête de satisfaction',
        texte: 'Le rappel pour répondre à l\'enquête de satisfaction s\'affiche sur le tableau de bord si : le compte a plus de 6 mois d\'ancienneté, au moins une société a un CA renseigné, aucune réponse n\'a été saisie ou la dernière date de plus d\'un an, et la fonctionnalité est active côté back.',
        source: 'Front · HomeMain.vue (checkEnqueteModalConditions)',
    },
    {
        cle: 'dashboard.sondageSatisfactionRelance',
        section: 'dashboard',
        titre: 'Répondre plus tard',
        texte: 'Cliquer sur RÉPONDRE PLUS TARD incrémente un compteur. À partir du 2ᵉ clic, deux options apparaissent : « Me relancer dans 3 mois » (le rappel disparaît pendant 3 mois précisément) ou « Ne plus demander » (le rappel disparaît jusqu\'au 1ᵉʳ janvier de l\'année suivante, pas définitivement). À partir du 3ᵉ clic, RÉPONDRE PLUS TARD disparaît et seules ces deux options restent possibles. Le compteur de clics est lui-même remis à zéro chaque 1er janvier.',
        source: 'Front · EnqueteModalContent.vue (checkClickCount, checkNewYearReset, handleRelanceSelection) ; SatisfactionService.ts (modalGestionOptions, dateRepondrePlusTard, dateNePlusAfficher)',
    },

    // --- Création d'un tableau de bord ---
    {
        cle: 'dashboard.creationNomObligatoire',
        section: 'dashboard',
        titre: 'Nom du tableau de bord',
        texte: 'SUIVANT reste désactivé à la 1ʳᵉ étape tant qu\'aucun nom n\'est saisi pour le nouveau tableau de bord.',
        source: 'Front · ModaleCreationDashboard.vue (isButtonDisabled, step 1)',
    },
    {
        cle: 'dashboard.creationSitesComportement',
        section: 'dashboard',
        titre: 'Sélection des sites',
        texte: 'Si aucun site n\'est explicitement choisi, tous les sites disponibles sont sélectionnés par défaut. Le bouton TOUS LES SITES n\'ajoute l\'ensemble des sites que si la sélection est entièrement vide : dès qu\'un seul site est déjà sélectionné (même partiellement), cliquer sur TOUS LES SITES vide la sélection au lieu de la compléter.',
        source: 'Front · ModaleCreationDashboard.vue (selectionnerTousLesSites, sitesSelectionnees)',
    },
    {
        cle: 'dashboard.editionPerimetrePropagationWidgets',
        section: 'dashboard',
        titre: 'Propager les widgets',
        texte: 'Quand on modifie le périmètre (les sites) d\'un tableau de bord existant, une case « propager aux widgets » permet d\'appliquer le même changement de périmètre à tous les widgets du tableau de bord ; décochée, les widgets gardent leur propre périmètre.',
        source: 'Front · ModaleCreationDashboard.vue (propagationWidgets, isOnlyForSite)',
    },

    // --- Édition du tableau de bord : création d'un widget ---
    {
        cle: 'editionDashboard.widgetPerimetreSites',
        section: 'editionDashboard',
        titre: 'Périmètre du widget',
        texte: 'Un widget peut être limité à un sous-ensemble des sites du tableau de bord (aucun site choisi = tous les sites). DUPLIQUER UN WIDGET ne reprend pas le périmètre du widget d\'origine : il repart sur l\'ensemble des sites du tableau de bord, à resélectionner.',
        source: 'Front · ModaleCreationWidget.vue (listeSitesSelectionnees, widgetADupliquer)',
    },
    {
        cle: 'editionDashboard.widgetSelectionComportement',
        section: 'editionDashboard',
        titre: 'Sélection des sites du widget',
        texte: 'Comme pour un tableau de bord, le bouton TOUS LES SITES vide la sélection dès qu\'au moins un site est déjà sélectionné, plutôt que de compléter la sélection. Taper dans le champ de recherche de site réinitialise aussi la sélection en cours.',
        source: 'Front · ModaleCreationWidget.vue (selectionnerTousLesSites, filtrerSite)',
    },
    {
        cle: 'editionDashboard.widgetDenominationObligatoire',
        section: 'editionDashboard',
        titre: 'Nom du widget',
        texte: 'AJOUTER (ou MODIFIER) reste désactivé à la dernière étape tant qu\'aucun nom n\'est saisi pour le widget.',
        source: 'Front · ModaleCreationWidget.vue (isInputDisabled, step 3)',
    },

    // --- Facturation ---
    {
        cle: 'facturation.definition',
        section: 'facturation',
        titre: 'Facturation',
        texte: 'Cette page liste les factures émises par le Groupe Hervé au compte client (travaux, interventions), réparties en 3 onglets selon leur statut de paiement (En retard, Échéance, Payées), plus un 4ᵉ onglet « Suivi » qui recoupe la facturation avec les interventions non préventives. Accessible uniquement aux comptes client ayant le droit de consulter les factures reçues.',
        source: 'Front · router/index.ts (droit consulter.facture.recue, Permissions.CLIENT) ; ListFacturesPage.vue',
    },
    {
        cle: 'facturation.perimetreChantiers',
        section: 'facturation',
        titre: 'Portée de la liste',
        texte: 'Chaque onglet (En retard, Échéance, Payées) affiche les factures du compte filtrées sur les sites transmis par l\'URL (par exemple depuis un widget du tableau de bord) ; sans ce filtre, tous les sites accessibles sont affichés.',
        source: 'Front · ListFacturesEnRetard.vue, ListFacturesAvantEcheance.vue, ListFacturesPayees.vue ($route.query.chantierIds)',
    },
    {
        cle: 'facturation.compteurFiltre',
        section: 'facturation',
        titre: 'Compteur affiché',
        texte: 'Le titre de l\'onglet affiche le nombre de résultats filtrés et, une fois un filtre actif, le rappelle à côté du total initial (mémorisé au premier chargement non filtré de la session).',
        source: 'Front · ListFacturesEnRetard.vue (totalCount, isFiltered)',
    },
    {
        cle: 'facturation.defilementInfini',
        section: 'facturation',
        titre: 'Chargement de la liste',
        texte: 'La liste se charge par défilement infini (page suivante chargée en approchant du bas), pas par pagination classique. Chaque onglet garde son état (filtres, tri, position de défilement) quand on change d\'onglet.',
        source: 'Front · ListFacturesEnRetard.vue (handleScroll) ; ListFacturesPage.vue (keep-alive)',
    },
    {
        cle: 'facturation.triLocal',
        section: 'facturation',
        titre: 'Tri du tableau',
        texte: 'Le tri (clic sur un en-tête de colonne) s\'applique uniquement aux lignes déjà chargées en mémoire, sans recharger depuis le serveur : si toutes les pages n\'ont pas encore été parcourues par défilement, le tri peut être incomplet sur l\'ensemble des résultats.',
        source: 'Front · ListFacturesEnRetard.vue (sortDataClientSide) ; composables/useSortDataClientSide.ts',
    },
    {
        cle: 'facturation.telechargementPdfConditionnel',
        section: 'facturation',
        titre: 'Télécharger le PDF',
        texte: 'Le téléchargement du PDF ne se déclenche que si le document existe pour la facture (ou, dans l\'onglet Suivi, si l\'intervention est facturée) ; sinon le clic ne fait rien, sans message d\'erreur.',
        source: 'Front · ListFacturesEnRetard.vue (downloadPdf, pdfExist) ; SuiviFinancier.vue (downloadPdf, facturee)',
    },
    {
        cle: 'facturation.echeanceFenetreFixe',
        section: 'facturation',
        titre: 'Onglet Échéance',
        texte: 'L\'onglet ÉCHÉANCE ne liste que les factures dont l\'échéance est dans les 15 jours, fenêtre fixe non modifiable depuis l\'écran.',
        source: 'Front · ListFacturesAvantEcheance.vue (joursAvantEcheance: 15)',
    },
    {
        cle: 'facturation.filtreDatesIncoherentes',
        section: 'facturation',
        titre: 'Filtre par dates',
        texte: 'Si la date de fin saisie est antérieure à la date de début, la date de début est silencieusement réinitialisée plutôt que de bloquer la recherche ou d\'afficher une erreur.',
        source: 'Front · FilterSuiviFacture.vue (setFilterDateTo) ; SuiviFinancier.vue (setCreatedDateTo)',
    },
    {
        cle: 'facturation.suiviFinancierChoixSite',
        section: 'facturation',
        titre: 'Suivi financier : choix du site',
        texte: 'L\'onglet SUIVI s\'ouvre sur une sélection de sites obligatoire avant d\'afficher la liste, sauf si le compte n\'a qu\'un seul site (sélectionné automatiquement).',
        source: 'Front · SuiviFinancier.vue (displayModalChoixSite, isUniqueSite)',
    },
    {
        cle: 'facturation.suiviFinancierExport',
        section: 'facturation',
        titre: 'Exporter en XLS',
        texte: 'Le bouton d\'export Excel n\'apparaît que lorsque la liste a fini de charger et contient au moins un résultat.',
        source: 'Front · SuiviFinancier.vue (export-button)',
    },

    // --- Boutons d'action rapide (en-tête) ---
    {
        cle: 'menuCompte.boutonDemanderDevis',
        section: 'menuCompte',
        titre: 'Demander un devis',
        texte: 'Visible dès qu\'on est connecté, sur toutes les pages du portail client — mais pas sur les pages du portail fournisseur (dashboard-fournisseur, factures, règlements). Aucun droit métier particulier n\'est requis.',
        source: 'Front · MainHeader.vue (bouton-demande-devis, isPortailClient)',
    },
    {
        cle: 'menuCompte.boutonDemandeIntervention',
        section: 'menuCompte',
        titre: "Demande d'intervention",
        texte: 'Même condition que DEMANDER UN DEVIS (portail client, pas fournisseur), avec en plus le droit creer.demande requis : contrairement au bouton Devis, ce bouton n\'apparaît donc pas pour tous les comptes client.',
        source: 'Front · MainHeader.vue (bouton-demande-intervention, checkDroitSubRoute(\'creer.demande\'), isPortailClient)',
    },

    // --- Menu du compte (en-tête) ---
    {
        cle: 'menuCompte.deconnexion',
        section: 'menuCompte',
        titre: 'Déconnexion',
        texte: 'SE DÉCONNECTER vide le cache MSAL et les jetons stockés localement (token Azure, jeton métier), clôture la session Azure AD, puis renvoie vers la page de connexion.',
        source: 'Front · MainHeader.vue (disconnect)',
    },
    {
        cle: 'menuCompte.optionsInterventions',
        section: 'menuCompte',
        titre: 'Options interventions',
        texte: 'OPTIONS INTERVENTIONS n\'apparaît que pour un compte reconnu comme client ; absent pour un compte fournisseur. Ouvre la modale de configuration des options de suivi des demandes d\'intervention.',
        source: 'Front · MainHeader.vue (isClient) ; OptionSuiviModale.vue',
    },
    {
        cle: 'menuCompte.preavisIntervention',
        section: 'menuCompte',
        titre: 'Préavis d\'intervention',
        texte: 'PRÉAVIS D\'INTERVENTION n\'apparaît que pour un compte client, comme OPTIONS INTERVENTIONS ; absent pour un compte fournisseur. Ouvre la modale de gestion des préavis d\'intervention.',
        source: 'Front · MainHeader.vue (isClient) ; PreavisInterventionModale.vue',
    },
    {
        cle: 'menuCompte.monCompte',
        section: 'menuCompte',
        titre: 'Mon compte',
        texte: 'MON COMPTE est visible pour tous les comptes connectés, client comme fournisseur, et ouvre la modale d\'informations du compte.',
        source: 'Front · MainHeader.vue (openMonCompteModale) ; MonCompteModale.vue',
    },

    // --- Modale Options interventions (suivi personnalisé des demandes d'intervention) ---
    {
        cle: 'optionsInterventions.maxEmails',
        section: 'optionsInterventions',
        titre: 'Emails de suivi',
        texte: 'Quatre adresses email actives maximum peuvent être enregistrées pour le suivi personnalisé des demandes d\'intervention. Au-delà, le champ d\'ajout est désactivé avec le message « Nombre d\'emails de suivi actifs maximum atteint ».',
        source: 'Front · OptionSuiviModale.vue ; store/getters.ts (isMaximumEmailActifReached, >= 4)',
    },
    {
        cle: 'optionsInterventions.desactivationGlobale',
        section: 'optionsInterventions',
        titre: 'Activer le suivi',
        texte: 'Décocher « Activer le suivi personnalisé de mes demandes d\'intervention » grise la liste et bloque l\'ajout, la suppression et l\'activation/désactivation individuelle de chaque adresse email.',
        source: 'Front · OptionSuiviModale.vue (classe disabled) ; store/actions.ts (traitementEmailSuivi, gardé par suiviDI.actif)',
    },
    {
        cle: 'optionsInterventions.formatEmail',
        section: 'optionsInterventions',
        titre: 'Format de l\'email',
        texte: 'AJOUTER n\'est actif que si l\'adresse saisie contient un « @ » suivi d\'un point (format minimal, pas de vérification complète du domaine).',
        source: 'Front · store/actions.ts (AJOUTER_EMAIL_SUIVI, regex /\\S+@\\S+\\.\\S+/)',
    },

    // --- Modale Mon compte ---
    {
        cle: 'monCompte.champsEnLectureSeule',
        section: 'monCompte',
        titre: 'Identifiant / Nom / Prénom',
        texte: 'L\'identifiant, le nom et le prénom sont en lecture seule. Seuls le mot de passe et les numéros de téléphone sont modifiables, ligne par ligne, avec un enregistrement individuel immédiat (pas de bouton de validation global pour toute la fiche).',
        source: 'Front · MonCompteModale.vue (DataList en lecture seule, EditableInput pour mdp/téléphones)',
    },
    {
        cle: 'monCompte.motDePasseRegles',
        section: 'monCompte',
        titre: 'Mot de passe',
        texte: 'Le nouveau mot de passe doit comporter entre 8 et 64 caractères et respecter au moins 3 des 4 critères suivants : une majuscule, une minuscule, un chiffre, un caractère spécial. ENREGISTRER reste désactivé tant que ces conditions ne sont pas remplies.',
        source: 'Front · EditableInput.vue (isMdpValid, isMdpLengthValid) ; common/utils/Regex.ts',
    },
    {
        cle: 'monCompte.suppressionCompte',
        section: 'monCompte',
        titre: 'Demander la suppression du compte',
        texte: 'La demande de suppression du compte ne peut être faite qu\'une fois : après confirmation, le bouton affiche « Suppression déjà demandée » et reste désactivé.',
        source: 'Front · MonCompteModale.vue (isDeleteAccountRequested)',
    },

    // --- Préavis d'intervention ---
    {
        cle: 'preavisIntervention.definition',
        section: 'preavisIntervention',
        titre: "Préavis d'intervention",
        texte: 'Un préavis d\'intervention associe un équipement à une alerte email envoyée avant une échéance : un objet, un délai en jours et jusqu\'à 5 adresses email de suivi.',
        source: 'Front · PreavisFormCreateEdit.vue ; PreavisCreateModal.vue (CREATE)',
    },
    {
        cle: 'preavisIntervention.portee',
        section: 'preavisIntervention',
        titre: 'Portée de la liste',
        texte: 'Le tableau liste tous les préavis d\'intervention du compte client, filtrables par site et par équipement ; il se réactualise à chaque recherche, tri, ou fermeture/réouverture de la modale.',
        source: 'Front · PreavisConsultModal.vue (search, watch visible)',
    },
    {
        cle: 'preavisIntervention.delaiMax',
        section: 'preavisIntervention',
        titre: 'Délai',
        texte: 'Le délai se choisit entre 1 et 15 jours avant l\'échéance, dans une liste déroulante (pas de saisie libre).',
        source: 'Front · constantes.ts (MAX_DELAI_PREAVIS = 15) ; PreavisFormCreateEdit.vue',
    },
    {
        cle: 'preavisIntervention.emailsRegles',
        section: 'preavisIntervention',
        titre: 'Emails d\'alerte',
        texte: '5 adresses email maximum par préavis. L\'email du créateur est ajouté automatiquement à la création et ne peut pas être supprimé ; les doublons sont refusés.',
        source: 'Front · constantes.ts (MAX_MAIL_PREAVIS = 5) ; PreavisFormCreateEdit.vue (lockedEmail, isDuplicateEmail)',
    },
    {
        cle: 'preavisIntervention.conflitEquipement',
        section: 'preavisIntervention',
        titre: 'Équipement déjà sous préavis',
        texte: 'Sélectionner manuellement un équipement qui a déjà un préavis actif bloque la validation (message d\'erreur). Via SÉLECTIONNER TOUT, ces équipements sont au contraire exclus silencieusement de la sélection, sans bloquer.',
        source: 'Front · PreavisCreateModal.vue (manuallyConflictingEquipement, autoExcludedEquipement, selectAllPerformed)',
    },
    {
        cle: 'preavisIntervention.objetParDefaut',
        section: 'preavisIntervention',
        titre: 'Objet pré-rempli',
        texte: 'En création, l\'objet est pré-rempli automatiquement à partir du nom de l\'équipement sélectionné (générique si plusieurs équipements) ; modifiable, mais obligatoire pour valider.',
        source: 'Front · PreavisCreateModal.vue (initialiserObjet) ; PreavisFormCreateEdit.vue (objetOk)',
    },
    {
        cle: 'preavisIntervention.fermetureReinitialise',
        section: 'preavisIntervention',
        titre: 'Fermer la création',
        texte: 'Fermer la modale de création réinitialise entièrement le formulaire (étape, site, équipements, objet, emails) : la rouvrir ne reprend pas la saisie précédente.',
        source: 'Front · PreavisCreateModal.vue (watch visible, reset)',
    },

    // --- Outils (suivi énergétique / déclaration de compteurs) ---
    {
        cle: 'outils.definition',
        section: 'outils',
        titre: 'Votre suivi énergétique',
        texte: 'Cette page (nommée « Outils » en interne) sert à transmettre au Groupe Hervé les numéros de compteurs énergétiques d\'une société, avec leur facture justificative, via un formulaire en 3 étapes. Elle ne lit ni n\'affiche aucune donnée existante : tout est ressaisi à chaque envoi, et le formulaire est entièrement réinitialisé après confirmation.',
        source: 'Front · router/index.ts (/outils) ; OutilsPage.vue ; store/state.ts (CompteurState)',
    },
    {
        cle: 'outils.etape1Obligatoire',
        section: 'outils',
        titre: 'Informations société',
        texte: 'Tous les champs de cette étape sont obligatoires (raison sociale, nom commercial, numéro d\'identification, adresse, code postal, ville) pour passer à l\'étape suivante.',
        source: 'Front · store/getters.ts (isCompteurFormValid, step 0)',
    },
    {
        cle: 'outils.etape2AdresseConditionnelle',
        section: 'outils',
        titre: 'Adresse du représentant',
        texte: 'Nom, prénom, date de naissance et email du représentant sont toujours obligatoires. L\'adresse n\'est demandée que si elle diffère de celle de la société (sinon celle-ci est reprise automatiquement).',
        source: 'Front · store/getters.ts (isCompteurFormValid, step 1) ; StepTwoCompteur.vue (adresseIdentique)',
    },
    {
        cle: 'outils.etape3Compteurs',
        section: 'outils',
        titre: 'Ajouter un compteur',
        texte: 'AJOUTER UN COMPTEUR reste désactivé tant que la ligne en cours n\'a ni numéro de compteur ni facture jointe. Une fois une nouvelle ligne ajoutée, les lignes précédentes deviennent en lecture seule (seule la suppression reste possible, plus la modification). Si la dernière ligne est laissée vide, elle est automatiquement retirée avant l\'envoi.',
        source: 'Front · StepThreeCompteur.vue (ajoutDisabled) ; CompteurDataTable.vue (isLastItem) ; OutilsPage.vue (sendMail)',
    },
    {
        cle: 'outils.reinitialisationApresEnvoi',
        section: 'outils',
        titre: 'Après l\'envoi',
        texte: 'Une fois l\'envoi confirmé, fermer la fenêtre d\'information renvoie à l\'accueil et réinitialise entièrement le formulaire : rien n\'est conservé pour un envoi ultérieur.',
        source: 'Front · OutilsPage.vue (goHomePage, COMPTEUR.RESET)',
    },

    // --- Saisir une réclamation ---
    {
        cle: 'reclamation.definition',
        section: 'reclamation',
        titre: 'Saisir une réclamation',
        texte: 'Une réclamation porte sur un site précis (étape 1) et une description libre (étape 2, obligatoire) ; aucune pièce jointe n\'est proposée. Une fois envoyée, un numéro de réclamation est attribué et communiqué en confirmation.',
        source: 'Front · ReclamationPage.vue (request) ; services/ReclamationService.ts (sendReclamation)',
    },
    {
        cle: 'reclamation.etapesObligatoires',
        section: 'reclamation',
        titre: 'Validation des étapes',
        texte: 'SUIVANT (étape 1 → 2) n\'est actif qu\'après avoir choisi un site ; ENVOYER n\'est actif qu\'avec une description non vide (les espaces seuls ne comptent pas).',
        source: 'Front · ReclamationPage.vue (isValid) ; StepTwo.vue (isValid)',
    },

    // --- Voir toutes les réclamations (suivi) ---
    {
        cle: 'suiviReclamation.definition',
        section: 'suiviReclamation',
        titre: 'Suivi des réclamations',
        texte: 'Liste toutes les réclamations du compte, triées par défaut par numéro décroissant (les plus récentes en premier). Cliquer sur une ligne ouvre son historique de traitement.',
        source: 'Front · SuiviReclamationPage.vue (doSelect) ; store/state.ts (tri par défaut sur id, DESC)',
    },
    {
        cle: 'suiviReclamation.clotureReouverture',
        section: 'suiviReclamation',
        titre: 'Clôturer / réouvrir',
        texte: 'Dans l\'historique, seule l\'action encore possible est proposée : CLÔTURER si la réclamation n\'est pas déjà close, RÉOUVRIR sinon. CLÔTURER agit immédiatement, sans confirmation ni motif. RÉOUVRIR exige un motif et crée en réalité une nouvelle réclamation liée à l\'ancienne, via le même mécanisme que SAISIR UNE RÉCLAMATION.',
        source: 'Front · SuiviReclamationPage.vue (cloturer, reouvrirReclamation, typeActionId === 4)',
    },
    {
        cle: 'suiviReclamation.filtreStatut',
        section: 'suiviReclamation',
        titre: 'Filtre par statut',
        texte: 'Par défaut, tous les statuts sont cochés (aucun filtre réel appliqué). Décocher un ou plusieurs statuts restreint la liste ; si tous les statuts possibles restent cochés, aucun filtre n\'est transmis au serveur.',
        source: 'Front · SuiviReclamationPage.vue (getReclamationsData, filterStatus.length === statusList.length)',
    },
    {
        cle: 'suiviReclamation.export',
        section: 'suiviReclamation',
        titre: 'Exporter en XLS',
        texte: 'Le bouton d\'export est désactivé si la liste ne contient aucun résultat.',
        source: 'Front · SuiviReclamationPage.vue (isExportDisabled)',
    },

    // --- Satisfaction (page d'accueil : choix de la note) ---
    {
        cle: 'satisfaction.definition',
        section: 'satisfaction',
        titre: 'Satisfaction',
        texte: 'Cette page permet de noter sa satisfaction (3 smileys) pour une société du compte, ce qui ouvre un questionnaire dont le contenu dépend de la note donnée. Réservée aux comptes client (jamais fournisseur).',
        source: 'Front · router/index.ts (route /satisfaction dans routesHeader, Permissions.CLIENT) ; Satisfaction.vue',
    },
    {
        cle: 'satisfaction.visibiliteMenu',
        section: 'satisfaction',
        titre: 'Affichage du menu Satisfaction',
        texte: 'Le menu « Satisfaction » de la barre latérale n\'apparaît que si le paramétrage du compte (table MODALE_SATISFACTION_ACTIVE, par compte et par domaine) l\'autorise ; sinon l\'entrée est retirée du menu, même pour un compte client. C\'est un réglage distinct des conditions du rappel sur le tableau de bord (ancienneté du compte, CA renseigné...), bien qu\'il s\'appuie sur la même information renvoyée par le serveur.',
        source: 'Front · Layout.vue (menu(), filtre sur modalConditionDTO.active) ; models/Satisfaction.ts (ModalConditionDTO.active)',
    },
    {
        cle: 'satisfaction.choixSociete',
        section: 'satisfaction',
        titre: 'Choix de la société',
        texte: 'La liste déroulante « Quelle société souhaitez-vous évaluer ? » ne s\'affiche que si l\'appel société-ids (filtré sur filtreSitePublie=true, donc uniquement les sociétés ayant au moins un site publié) renvoie plusieurs sociétés : il faut alors en choisir une avant que les smileys n\'apparaissent. Si une seule société est renvoyée (y compris si le compte a plusieurs sociétés mais qu\'une seule a un site publié), elle est sélectionnée automatiquement et les smileys s\'affichent directement, sans liste déroulante.',
        source: 'Front · Satisfaction.vue (beforeMount, getSocieteIds(filtreSitePublie=true), getSocietes.length)',
    },
    {
        cle: 'satisfaction.parcoursSelonNote',
        section: 'satisfaction',
        titre: 'Parcours selon la note',
        texte: 'Le smiley neutre lance un parcours de questions différent (noté « 1/5 » en interne) de celui des smileys content ou mécontent (noté « 1/4 ») : le nombre et le contenu des questions posées ensuite dépendent de la note choisie.',
        source: 'Front · Satisfaction.vue (handleClick, numeroQuestionSaisie)',
    },

    // --- Questionnaire de satisfaction ---
    {
        cle: 'questionnaireSatisfaction.definition',
        section: 'questionnaireSatisfaction',
        titre: 'Questionnaire de satisfaction',
        texte: 'Les questions (texte libre, cases à cocher, choix oui/non, ou sélection de site) sont renvoyées par le serveur selon la note choisie sur la page précédente ; le nombre d\'étapes de la barre de progression varie donc d\'un parcours à l\'autre.',
        source: 'Front · QuestionPage.vue (fetchQuestions, RechercheQuestionsDTO)',
    },
    {
        cle: 'questionnaireSatisfaction.noteInsatisfaitSites',
        section: 'questionnaireSatisfaction',
        titre: 'Sites concernés (note insatisfaite)',
        texte: 'Une question peut demander de sélectionner un ou plusieurs sites concernés. Le message de fin de questionnaire diffère selon la note : pour la note la plus basse (insatisfait), il fait explicitement référence à une réclamation.',
        source: 'Front · QuestionPage.vue (type CHOIX_SITE_RECLAMATION, note === 1)',
    },
    {
        cle: 'questionnaireSatisfaction.limitesTexteLibre',
        section: 'questionnaireSatisfaction',
        titre: 'Réponse en texte libre',
        texte: 'Une réponse en texte libre doit faire au moins 10 caractères pour valider. Le maximum est de 1500 caractères pour la note la plus basse (insatisfait), 4000 caractères pour les autres notes.',
        source: 'Front · QuestionPage.vue (minCharacters, maxCharacters)',
    },
    {
        cle: 'questionnaireSatisfaction.optionAutre',
        section: 'questionnaireSatisfaction',
        titre: 'Option « Autre » (cases à cocher)',
        texte: 'Quand une liste de choix comporte une option « Autre » à champ libre et qu\'elle est cochée, le texte associé est limité à 350 caractères et ne peut pas commencer par un espace (les espaces de début sont retirés automatiquement). SUIVANT reste désactivé tant que ce champ est vide.',
        source: 'Front · QuestionPage.vue (updateOtherValueCharCount, isChampsLibre, activeValidate)',
    },

    // --- Contact ---
    {
        cle: 'contact.definition',
        section: 'contact',
        titre: 'Contacter un interlocuteur',
        texte: 'Envoie un message à un interlocuteur du Groupe Hervé rattaché à un site précis, en 3 étapes : choix du site, choix de l\'interlocuteur (liste propre à ce site), puis objet et description du message (4000 caractères max).',
        source: 'Front · ContactPage.vue ; store/getters.ts (isContactFormValid)',
    },
    {
        cle: 'contact.interlocuteursParSite',
        section: 'contact',
        titre: 'Liste des interlocuteurs',
        texte: 'La liste proposée à l\'étape 2 dépend uniquement du site choisi à l\'étape 1. Revenir en arrière et changer de site réinitialise l\'interlocuteur sélectionné et recharge la liste pour le nouveau site.',
        source: 'Front · store/mutations.ts (SET_SITE vide interlocuteurs) ; SelectInterlocuteur.vue (rechargement si liste vide)',
    },
    {
        cle: 'contact.exportContacts',
        section: 'contact',
        titre: 'Exporter les contacts',
        texte: 'Dès l\'étape 1 (choix du site), un bouton permet d\'exporter en XLS la liste des interlocuteurs du site filtré, sans aller jusqu\'au bout du formulaire. Contrairement à d\'autres écrans utilisant le même sélecteur de site, les sites de chantiers terminés sont inclus ici dans la recherche.',
        source: 'Front · SelectSiteContact.vue (export-contact, inclure-termines) ; SelectSite.vue (exportXlsContact)',
    },
    {
        cle: 'suiviReclamation.colonneEtat',
        section: 'suiviReclamation',
        titre: 'Colonne État',
        texte: 'Une réclamation passe par 4 états : « À qualifier » (déposée, pas encore prise en charge), « À traiter », « En traitement », puis « Clôturée ». RÉOUVRIR depuis l\'historique fait repartir la réclamation sur un nouveau cycle (elle quitte l\'état « Clôturée »).',
        source: 'Front · models/SuiviReclamation.ts (EtatReclamation) ; StatusTag.vue (statusName) ; locales/fr.json (state.A_QUALIFIER, state.A_TRAITER, state.EN_TRAITEMENT, state.CLOTUREE)',
    },

    // --- Définitions métier manquantes sur le menu Interventions (ajoutées a posteriori) ---
    {
        cle: 'demandeIntervention.definition',
        section: 'demandeIntervention',
        titre: 'Demande d\'intervention',
        texte: 'Une demande d\'intervention permet de solliciter un technicien pour un dépannage ou des travaux ponctuels sur un équipement ou un site, en dehors d\'un contrat de maintenance préventive.',
        source: 'Front · InterventionPage.vue ; models/DemandeIntervention.ts',
    },
    {
        cle: 'interventionPreventive.definition',
        section: 'suiviInterventionsPreventives',
        titre: 'Intervention préventive',
        texte: 'Une intervention préventive est un passage de maintenance programmé à l\'avance dans le cadre d\'un contrat, par opposition à une intervention réalisée au fil de l\'eau.',
        source: 'Front · models/InterventionPreventive.ts',
    },
    {
        cle: 'interventionNonPreventive.definition',
        section: 'suiviInterventionsNonPreventives',
        titre: 'Intervention non préventive',
        texte: 'Une intervention non préventive est un passage technique réalisé hors contrat de maintenance, le plus souvent déclenché par une demande d\'intervention ponctuelle.',
        source: 'Front · models/InterventionNonPreventive.ts',
    },
    {
        cle: 'rapportsASigner.definition',
        section: 'rapportsASigner',
        titre: 'Rapport à signer',
        texte: 'Un rapport d\'intervention doit être signé par le client pour valider que les travaux décrits ont bien été réalisés.',
        source: 'Front · RapportASigner.vue (signerRapports)',
    },
    {
        cle: 'demandeDevis.definition',
        section: 'demandeDevis',
        titre: 'Demande de devis',
        texte: 'Une demande de devis permet de solliciter un chiffrage pour des travaux ou prestations, avant toute décision d\'engagement.',
        source: 'Front · EstimatePage.vue ; models/DemandeDevis.ts',
    },
    {
        cle: 'devis.definition',
        section: 'suiviDevis',
        titre: 'Devis',
        texte: 'Un devis est le chiffrage transmis en réponse à une demande ; le client doit ensuite le commander ou le refuser.',
        source: 'Front · models/Devis.ts ; DevisDetailPage.vue (command, refuse)',
    },

    // --- Tableau de bord fournisseur ---
    {
        cle: 'dashboardFournisseur.definition',
        section: 'dashboardFournisseur',
        titre: 'Tableau de bord fournisseur',
        texte: 'Vue de synthèse pour les comptes fournisseurs : indicateurs de factures et derniers règlements reçus. Contrairement au tableau de bord client, il n\'est pas personnalisable.',
        source: 'Front · DashboardFournisseur.vue (WidgetFacture, WidgetReglement, WidgetDocument)',
    },
    {
        cle: 'dashboardFournisseur.coordonneesFacturation',
        section: 'dashboardFournisseur',
        titre: 'Coordonnées de facturation',
        texte: 'Le lien « Détails » ouvre la liste des adresses email à utiliser pour envoyer les factures, une par société du groupe.',
        source: 'Front · DashboardFournisseur.vue (societeTriees, mails)',
    },
    {
        cle: 'dashboardFournisseur.statutsFactures',
        section: 'dashboardFournisseur',
        titre: 'Statuts des factures',
        texte: 'Compte, sur les deux dernières années, les factures par statut : À valider, Validées, En litige, Rejetées, Payées. Cliquer sur un statut ouvre la liste des factures filtrée sur ce statut.',
        source: 'Front · WidgetFacture.vue (getStatsFactures, setFilterAValider...)',
    },
    {
        cle: 'dashboardFournisseur.derniersReglements',
        section: 'dashboardFournisseur',
        titre: 'Derniers règlements',
        texte: 'Affiche les 5 derniers règlements reçus ; cliquer sur une ligne ouvre le détail du règlement.',
        source: 'Front · WidgetReglement.vue (getDerniersReglements, goToReglement)',
    },

    // --- Factures fournisseur (liste) ---
    {
        cle: 'facturesFournisseur.definition',
        section: 'facturesFournisseur',
        titre: 'Facture fournisseur',
        texte: 'Une facture fournisseur est une facture émise par le compte fournisseur pour des prestations réalisées pour le Groupe Hervé ; elle doit être validée avant mise en paiement.',
        source: 'Front · models/FactureFournisseurDTO.ts',
    },
    {
        cle: 'facturesFournisseur.statutRejeteeExclusif',
        section: 'facturesFournisseur',
        titre: 'Statut Rejetée',
        texte: 'Le statut Rejetée est exclusif des autres : le sélectionner retire tous les autres statuts, et en sélectionner un autre le retire. « Tout sélectionner » ne l\'inclut jamais : les factures rejetées s\'affichent toujours à part.',
        source: 'Front · FacturesFournisseurList.vue (addFilterStatus, statusToggleSelectAll)',
    },
    {
        cle: 'facturesFournisseur.filtreParDefaut',
        section: 'facturesFournisseur',
        titre: 'Filtre par défaut',
        texte: 'Par défaut, les factures Rejetées ne sont pas affichées : il faut sélectionner ce statut explicitement pour les voir.',
        source: 'Front · FacturesFournisseurList.vue (beforeMount)',
    },

    // --- Facture fournisseur (détail) ---
    {
        cle: 'factureFournisseurDetail.contacterRecipient',
        section: 'factureFournisseurDetail',
        titre: 'Contacter',
        texte: 'CONTACTER n\'envoie pas au même interlocuteur selon le statut de la facture : pour une facture Rejetée, le message part au comptable ; sinon, à l\'assistante référente.',
        source: 'Front · FactureFournisseurPage.vue (isFactureRejetee, envoyer)',
    },
    {
        cle: 'factureFournisseurDetail.historiqueRejets',
        section: 'factureFournisseurDetail',
        titre: 'Historique',
        texte: 'Liste les rejets précédents de cette facture (même numéro), avec leur cause et leur commentaire.',
        source: 'Front · FactureFournisseurPage.vue (historiqueFacture, isHistorique: true)',
    },
    {
        cle: 'factureFournisseurDetail.reglementsListe',
        section: 'factureFournisseurDetail',
        titre: 'Règlements',
        texte: 'La liste des règlements n\'apparaît que si la facture en a déjà reçu au moins un ; cliquer sur un règlement ouvre son détail.',
        source: 'Front · FactureFournisseurPage.vue (factureData.reglements)',
    },

    // --- Règlements fournisseur (liste) ---
    {
        cle: 'reglementsFournisseur.definition',
        section: 'reglementsFournisseur',
        titre: 'Règlement fournisseur',
        texte: 'Un règlement fournisseur est un paiement reçu du Groupe Hervé, qui peut couvrir une ou plusieurs factures.',
        source: 'Front · models/ReglementDTO.ts',
    },

    // --- Règlement fournisseur (détail) ---
    {
        cle: 'reglementFournisseurDetail.facturesLiees',
        section: 'reglementFournisseurDetail',
        titre: 'Factures couvertes',
        texte: 'Liste les factures couvertes par ce règlement ; cliquer sur une facture ouvre son détail.',
        source: 'Front · ReglementFournisseurPage.vue (res.data.factures)',
    },

    // --- Actualités et communications (tableau de bord) ---
    {
        cle: 'dashboard.actualites',
        section: 'dashboard',
        titre: 'Actualités et communications',
        texte: 'Un panneau d\'actualités s\'ouvre automatiquement à la connexion s\'il y a des actualités non lues ; sinon, il reste réduit en un onglet latéral à ouvrir manuellement.',
        source: 'Front · Layout.vue (isDashboard) ; Actualites.vue (openOnStart, nbNouvellesActualites)',
    },
    {
        cle: 'dashboard.actualiteLectureAutomatique',
        section: 'dashboard',
        titre: 'Lecture automatique',
        texte: 'Une actualité est marquée comme lue dès qu\'elle s\'affiche dans le carrousel, sans action de l\'utilisateur.',
        source: 'Front · Actualites.vue (handleSlideStart, handleInit, setActualiteConsulte)',
    },
];

export const REGLES_PAR_CLE: Record<string, RegleGestion> = Object.fromEntries(
    REGLES_GESTION.map(regle => [regle.cle, regle])
);
